import { useCallback, useRef, useState } from "react";
import { toast } from "@/components/ui/toast";
import { standardAuthFetch } from "@/data-access/core/authenticatedFetch";
import { agenticPdfUrl } from "@/data-access/herald-chat/chat-http";
import { resolveHeraldPdfTailwindStylesheetUrl } from "@/lib/chat/resolveHeraldPdfTailwindStylesheetUrl";
import heraldAssistantPdfCss from "@/styles/herald-assistant-pdf.css?raw";

const FALLBACK_FILENAME = "herald.pdf";

const HERALD_ASSISTANT_PDF_STYLE_TAG = `<style>${heraldAssistantPdfCss}</style>`;

export interface DownloadHtmlAsPdfOptions {
  /** Sent as conversation_id for server-side naming. */
  conversationId?: string;
}

export interface UseDownloadHtmlAsPdfReturn {
  /** Toasts on failure; resolves (no rejection). */
  readonly download: (
    html: string,
    options?: DownloadHtmlAsPdfOptions,
  ) => Promise<void>;
  /** True while a `download` call is in flight (success or error). */
  readonly isPdfGenerating: boolean;
}

export const useDownloadHtmlAsPdf = (): UseDownloadHtmlAsPdfReturn => {
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const inFlightRef = useRef(false);

  const download = useCallback(
    async (html: string, options?: DownloadHtmlAsPdfOptions): Promise<void> => {
      if (inFlightRef.current) {
        return;
      }
      inFlightRef.current = true;
      setIsPdfGenerating(true);
      try {
        const conversationId = options?.conversationId?.trim();
        const tailwindStylesheetUrl = resolveHeraldPdfTailwindStylesheetUrl();
        const body: {
          html: string;
          conversation_id?: string;
          herald_pdf_style_tag: string;
          tailwind_stylesheet_url?: string;
        } = {
          html,
          herald_pdf_style_tag: HERALD_ASSISTANT_PDF_STYLE_TAG,
        };
        if (conversationId !== undefined && conversationId.length > 0) {
          body.conversation_id = conversationId;
        }
        if (tailwindStylesheetUrl !== null) {
          body.tailwind_stylesheet_url = tailwindStylesheetUrl;
        }

        const response = await standardAuthFetch(agenticPdfUrl, {
          method: "POST",
          body: JSON.stringify(body),
        });

        if (!response.ok) {
          throw new Error(await extractErrorMessage(response));
        }

        await triggerDownloadFromResponse(response);
        toast.success("Your PDF has been downloaded.");
      } catch (e) {
        const message =
          e instanceof Error && e.message.length > 0
            ? e.message
            : "PDF download failed";
        toast.error(message);
      } finally {
        inFlightRef.current = false;
        setIsPdfGenerating(false);
      }
    },
    [],
  );

  return { download, isPdfGenerating };
};

export const parseContentDispositionFilename = (
  header: string | null,
  fallback: string = FALLBACK_FILENAME,
): string => {
  if (!header) {
    return fallback;
  }

  const quoted = /filename="([^"]+)"/i.exec(header);
  if (quoted?.[1]) {
    return quoted[1];
  }

  const unquoted = /filename=([^;]+)/i.exec(header);
  if (unquoted?.[1]) {
    return unquoted[1].trim();
  }

  return fallback;
};

const extractErrorMessage = async (response: Response): Promise<string> => {
  try {
    const body = (await response.json()) as Record<string, unknown> | null;
    const error = body?.error;
    if (typeof error === "string" && error) return error;
    const detail = body?.detail;
    if (typeof detail === "string" && detail) return detail;
  } catch {}
  return `PDF rendering failed (${response.status})`;
};

const triggerDownloadFromResponse = async (
  response: Response,
): Promise<void> => {
  const filename = parseContentDispositionFilename(
    response.headers.get("Content-Disposition"),
  );
  const blob = await response.blob();

  const objectUrl = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};
